import { createContext, useContext } from 'react'

interface Currency {
  code: string,
  symbol: string,
}

export interface LocalizationContextType {
  currency?: Currency,
  weight_scale?: string,
  temperature_scale?: string,
  date_format: string,
  time_format?: string,
  timezone?: string
}

// Mirrors Location.default_localization_context on the Ruby side. Use this
// (instead of `{}`) when a consumer needs to render without a Provider — the
// type contract requires date_format, so an empty object would be unsafe.
export const DEFAULT_LOCALIZATION_CONTEXT: LocalizationContextType = {
  weight_scale: 'imperial',
  temperature_scale: 'fahrenheit',
  currency: { code: 'USD', symbol: '$' },
  date_format: 'MM.DD.YYYY',
  time_format: 'HH:mm:ss',
  timezone: 'UTC'
}

export const LocalizationContext = createContext<LocalizationContextType>(DEFAULT_LOCALIZATION_CONTEXT)

// Returns the current tenant's IANA timezone (e.g. 'Asia/Tokyo'), falling back
// to 'UTC' when no localization context is provided. Use anywhere a frontend
// component needs to compute "now" or "today" — pass to moment.tz(tz) instead
// of plain moment(), so a remote admin viewing a different-zone tenant sees
// the tenant's wall clock rather than the browser's.
export const useTenantTimezone = (): string => {
  const ctx = useContext(LocalizationContext)
  return ctx?.timezone || 'UTC'
}
